import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { eventToBinding } from '@/lib/mnemonics/keys';
import {
  SCOPE_GLOBAL,
  SCOPE_PAGE,
  createScope,
  removeScope,
  resolve,
} from '@/lib/mnemonics/registry';

const ScopeContext = createContext(null);

export const useMnemonicScopeId = () => useContext(ScopeContext);

/**
 * Owns the single keydown listener for the whole app. Components never attach
 * their own — they register into the scoped registry, which is also what the
 * underline renderer reads, so display and dispatch can never disagree.
 */
export function MnemonicProvider({ children }) {
  const [globalScopeId, setGlobalScopeId] = useState(null);

  useEffect(() => {
    const id = createScope(SCOPE_GLOBAL, 'app');
    setGlobalScopeId(id);
    return () => removeScope(id);
  }, []);

  useEffect(() => {
    // capture phase so a mnemonic wins over any local handler, and so
    // preventDefault lands before macOS inserts the Option-glyph.
    const onKeyDown = (event) => {
      if (event.repeat) return;
      const char = eventToBinding(event);
      if (!char) return;
      const binding = resolve(char);
      if (!binding || binding.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      binding.handler(event);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return <ScopeContext.Provider value={globalScopeId}>{children}</ScopeContext.Provider>;
}

/**
 * Opens a nested scope. A dialog scope is exclusive — while it is mounted,
 * page and global mnemonics stop resolving.
 */
export function MnemonicScope({ type = SCOPE_PAGE, label = '', children }) {
  const [scopeId, setScopeId] = useState(null);

  useEffect(() => {
    const id = createScope(type, label);
    setScopeId(id);
    return () => removeScope(id);
  }, [type, label]);

  // children render immediately with a null scope; child effects run before the
  // parent's, so registration happens on the re-render once the id exists.
  const value = useMemo(() => scopeId, [scopeId]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}
